<script setup lang="ts">
import { computed } from 'vue';
import type { HitRecord } from './caseDetail';
import { RECORD_STATUSES, recordStatusAction, type RecordStatus } from './recordStatus';
import RecordReaderContent from './RecordReaderContent.vue';

const props = withDefaults(
  defineProps<{
    record: HitRecord;
    note?: string;
    status?: RecordStatus;
    canPrev: boolean;
    canNext: boolean;
    kbVisible: boolean;
  }>(),
  { status: 'unreviewed' }
);

const emit = defineEmits<{
  close: [];
  prev: [];
  next: [];
  'cycle-status': [];
  'update-note': [recordId: string, value: string];
}>();

const noteFieldId = computed(() => `record-note-${props.record.id}`);

// Setting a status is the pane's primary action, so an untouched record keeps
// the filled treatment rather than sitting level with Prev/Next.
const statusButtonClass = computed(() =>
  props.status === 'unreviewed'
    ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700'
    : RECORD_STATUSES[props.status].button
);

function onNoteInput(event: Event) {
  emit('update-note', props.record.id, (event.target as HTMLTextAreaElement).value);
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col" data-test="record-review-pane">
    <div class="flex flex-none items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
      <button
        type="button"
        aria-label="Close"
        class="flex h-8 w-8 flex-none items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:bg-gray-50"
        @click="emit('close')"
      >
        ✕
      </button>
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm font-semibold text-gray-900">{{ record.title }}</div>
        <div class="truncate font-mono text-xs text-gray-400">{{ record.meta }}</div>
      </div>
      <span
        v-if="kbVisible"
        class="flex-none rounded border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-500"
      >
        Esc to close
      </span>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto p-4">
      <div class="mx-auto max-w-2xl">
        <RecordReaderContent :record="record" />
      </div>
    </div>

    <div
      class="flex flex-none flex-col gap-2.5 border-t border-gray-200 bg-white p-3"
      style="padding-bottom: calc(0.75rem + env(safe-area-inset-bottom))"
    >
      <div class="flex flex-col gap-1">
        <label
          :for="noteFieldId"
          class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase"
        >
          My note
        </label>
        <textarea
          :id="noteFieldId"
          rows="1"
          :value="note ?? ''"
          placeholder="Only you can see this"
          class="[field-sizing:content] w-full resize-none rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-[13px] text-gray-900 outline-none placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white"
          data-test="record-note-field"
          @input="onNoteInput"
        ></textarea>
      </div>

      <div class="flex gap-2">
        <button
          type="button"
          :disabled="!canPrev"
          class="flex-none rounded-md border border-gray-300 px-3.5 py-2 text-xs font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          :class="canPrev && 'hover:bg-gray-50'"
          @click="emit('prev')"
        >
          Prev
        </button>
        <button
          type="button"
          class="flex-1 rounded-md border px-3.5 py-2 text-xs font-semibold"
          :class="statusButtonClass"
          :aria-label="recordStatusAction(status)"
          :title="recordStatusAction(status)"
          data-test="record-status-button"
          @click="emit('cycle-status')"
        >
          {{ RECORD_STATUSES[status].label }}
        </button>
        <button
          type="button"
          :disabled="!canNext"
          class="flex-none rounded-md border border-gray-300 px-3.5 py-2 text-xs font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          :class="canNext && 'hover:bg-gray-50'"
          @click="emit('next')"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>
